<!-- src/components/LoginModal.vue -->
<template>
  <!-- 模态框组件，用于显示登录/注册界面 -->
  <Modal
      v-model="visible"
      :title="`用户注册 登录 ${registerInfo.allow_register ? '新用户注册送' + registerInfo.new_user_bonus + '次免费查询' : '用户登录'}`"
      :mask-closable="false"
      :closable="true"
      width="450"
      class="auth-modal"
      :footer-hide="true"
      @on-cancel="handleClose"
  >
    <!-- 模态框主要内容区域 -->
    <div class="auth-container">
      <!-- 标签切换区域：新用户注册/老用户登录/找回密码 -->
      <div class="tab-links">
        <!-- 新用户注册标签 - 仅在允许注册时显示 -->
        <span
            v-if="registerInfo.allow_register"
            :class="{ active: activeTab === 'new' }"
            @click="activeTab = 'new'"
        >
          新用户注册
        </span>
        <!-- 老用户登录标签 -->
        <span
            :class="{ active: activeTab === 'old' }"
            @click="activeTab = 'old'"
        >
          老用户登录
        </span>
        <!-- 找回密码标签 -->
        <span
            :class="{ active: activeTab === 'reset' }"
            @click="activeTab = 'reset'"
        >
          找回密码
        </span>
      </div>

      <!-- 注册表单区域 - 仅在允许注册时显示 -->
      <div v-if="activeTab === 'new' && registerInfo.allow_register">
        <!-- 注册表单内容保持不变 -->
        <Form :label-width="80">
          <!-- 用户名输入项 -->
          <FormItem label="用户名">
            <Input
                v-model="registerForm.username"
                placeholder="请输入用户名"
                size="large"
                @on-change="checkUsername"
            >
              <Icon type="md-person" slot="prefix"/>
            </Input>
            <div v-if="usernameCheckMessage" :class="['check-message', usernameCheckMessage.type]">
              {{ usernameCheckMessage.text }}
            </div>
          </FormItem>

          <!-- 邮箱输入项 -->
          <FormItem label="邮箱">
            <Input
                v-model="registerForm.email"
                placeholder="请输入邮箱"
                size="large"
                @on-change="checkEmail"
            >
              <Icon type="md-mail" slot="prefix"/>
            </Input>
            <div v-if="emailCheckMessage" :class="['check-message', emailCheckMessage.type]">
              {{ emailCheckMessage.text }}
            </div>
          </FormItem>

          <!-- 密码输入项 -->
          <FormItem label="密码">
            <div class="password-input-wrapper">
              <Input
                  v-model="registerForm.password"
                  :type="showRegisterPassword ? 'text' : 'password'"
                  placeholder="请输入密码(包含数字和字母)"
                  size="large"
              >
                <Icon type="md-lock" slot="prefix"/>
              </Input>
              <span class="eye-icon" @click="showRegisterPassword = !showRegisterPassword">
                <Icon :type="showRegisterPassword ? 'md-eye' : 'md-eye-off'" />
              </span>
            </div>
          </FormItem>

          <!-- 确认密码输入项 -->
          <FormItem label="确认密码">
            <div class="password-input-wrapper">
              <Input
                  v-model="registerForm.confirmPassword"
                  :type="showRegisterConfirmPassword ? 'text' : 'password'"
                  placeholder="请再次输入密码"
                  size="large"
                  @on-blur="checkRegisterConfirmPassword"
              >
                <Icon type="md-lock" slot="prefix"/>
              </Input>
              <span class="eye-icon" @click="showRegisterConfirmPassword = !showRegisterConfirmPassword">
                <Icon :type="showRegisterConfirmPassword ? 'md-eye' : 'md-eye-off'" />
              </span>
            </div>
            <div v-if="confirmPasswordMessage" :class="['check-message', confirmPasswordMessage.type]">
              {{ confirmPasswordMessage.text }}
            </div>
          </FormItem>

          <!-- 邀请码输入项（可选） -->
          <FormItem label="邀请码">
            <Input
                v-model="registerForm.inviteCode"
                placeholder="邀请码（选填）没有可以不用写"
                size="large"
            >
              <Icon type="md-pricetag" slot="prefix"/>
            </Input>
          </FormItem>

          <!-- 图形验证码输入项 -->
          <FormItem label="验证码">
            <div class="captcha-row">
              <Input
                  v-model="registerForm.captchaCode"
                  placeholder="请输入图中的汉字"
                  size="large"
                  style="flex: 1"
              >
                <Icon type="md-image" slot="prefix"/>
              </Input>
              <div class="captcha-image-wrapper" @click="refreshRegisterCaptcha" title="点击刷新验证码">
                <img v-if="registerCaptchaImage" :src="registerCaptchaImage" alt="验证码" class="captcha-img"/>
                <span v-else class="captcha-loading">加载中</span>
              </div>
            </div>
          </FormItem>

          <!-- 邮箱验证码输入项 -->
          <FormItem label="邮箱验证码">
            <div class="verification-code">
              <Input
                  v-model="registerForm.verificationCode"
                  placeholder="请输入邮箱验证码"
                  size="large"                  style="flex: 1"
              >
                <Icon type="md-key" slot="prefix"/>
              </Input>
              <Button
                  type="primary"
                  @click="sendVerificationCode"
                  :disabled="isSendingCode || countdown > 0"                  style="margin-left: 10px"
              >
                {{ countdown > 0 ? `${countdown}秒后重新发送` : verificationButtonText }}
              </Button>
            </div>
          </FormItem>

          <!-- 注册按钮 -->
          <FormItem>
            <Button
                type="primary"
                long
                size="large"
                @click="handleRegister"
                :loading="registerLoading"
            >
              注册
            </Button>
          </FormItem>
        </Form>
      </div>

      <!-- 登录表单区域 -->
      <div v-if="activeTab === 'old' || !registerInfo.allow_register">
        <!-- 登录提示信息 - 仅在允许注册时显示 -->
        <div v-if="registerInfo.allow_register" class="login-tip">
          <p style="color: #dc3545">注意：新用户注册送{{registerInfo.new_user_bonus}}次免费查询</p>
        </div>
        <Form :label-width="80">
          <!-- 账号输入项（支持用户名或邮箱登录） -->
          <FormItem label="账号">
            <Input
                v-model="loginForm.username"
                placeholder="请输入用户名或邮箱"
                size="large"
            >
              <Icon type="md-person" slot="prefix"/>
            </Input>
          </FormItem>
          <!-- 密码输入项 -->
          <FormItem label="密码">
            <div class="password-input-wrapper">
              <Input
                  v-model="loginForm.password"
                  :type="showLoginPassword ? 'text' : 'password'"
                  placeholder="请输入密码"
                  size="large"
              >
                <Icon type="md-lock" slot="prefix"/>
              </Input>
              <span class="eye-icon" @click="showLoginPassword = !showLoginPassword">
                <Icon :type="showLoginPassword ? 'md-eye' : 'md-eye-off'" />
              </span>
            </div>
          </FormItem>
          <!-- 图形验证码输入项 -->
          <FormItem label="验证码">
            <div class="captcha-row">
              <Input
                  v-model="loginForm.captchaCode"
                  placeholder="请输入图中的汉字"
                  size="large"
                  style="flex: 1"
              >
                <Icon type="md-image" slot="prefix"/>
              </Input>
              <div class="captcha-image-wrapper" @click="refreshLoginCaptcha" title="点击刷新验证码">
                <img v-if="loginCaptchaImage" :src="loginCaptchaImage" alt="验证码" class="captcha-img"/>
                <span v-else class="captcha-loading">加载中</span>
              </div>
            </div>
          </FormItem>
          <!-- 滑块验证码：在文字验证码之后、登录按钮之前显示 -->
          <FormItem label="滑块验证">
            <SliderCaptcha
                ref="loginSliderCaptcha"
                @success="handleSliderSuccess"
                @fail="handleSliderFail"
            />
          </FormItem>
          <!-- 登录按钮 -->
          <FormItem>
            <Button
                type="primary"
                long
                size="large"
                @click="handleLogin"
                :loading="loginLoading"
            >
              登录
            </Button>
          </FormItem>
        </Form>
      </div>

      <!-- 找回密码表单区域 -->
      <div v-if="activeTab === 'reset'">
        <div class="reset-tip">
          <p>请输入您的注册邮箱，我们将发送验证码帮助您重置密码</p>
        </div>
        <Form :label-width="80">
          <!-- 邮箱输入项 -->
          <FormItem label="邮箱">
            <Input
                v-model="resetForm.email"
                placeholder="请输入注册时使用的邮箱"
                size="large"
                @on-change="checkResetEmail"
            >
              <Icon type="md-mail" slot="prefix"/>
            </Input>
            <div v-if="resetEmailMessage" :class="['check-message', resetEmailMessage.type]">
              {{ resetEmailMessage.text }}
            </div>
          </FormItem>

          <!-- 验证码输入项 -->
          <FormItem label="验证码">
            <div class="verification-code">
              <Input
                  v-model="resetForm.verificationCode"
                  placeholder="请输入邮箱验证码"
                  size="large"
                  style="flex: 1"
              >
                <Icon type="md-key" slot="prefix"/>
              </Input>
              <Button
                  type="primary"
                  @click="sendResetCode"
                  :disabled="isSendingResetCode || resetCountdown > 0"
                  style="margin-left: 10px"
              >
                {{ resetCountdown > 0 ? `${resetCountdown}秒后重新发送` : '发送验证码' }}
              </Button>
            </div>
          </FormItem>

          <!-- 新密码输入项 -->
          <FormItem label="新密码">
            <div class="password-input-wrapper">
              <Input
                  v-model="resetForm.newPassword"
                  :type="showResetNewPassword ? 'text' : 'password'"
                  placeholder="请输入新密码(包含数字和字母)"
                  size="large"
              >
                <Icon type="md-lock" slot="prefix"/>
              </Input>
              <span class="eye-icon" @click="showResetNewPassword = !showResetNewPassword">
                <Icon :type="showResetNewPassword ? 'md-eye' : 'md-eye-off'" />
              </span>
            </div>
          </FormItem>

          <!-- 确认密码输入项 -->
          <FormItem label="确认密码">
            <div class="password-input-wrapper">
              <Input
                  v-model="resetForm.confirmPassword"
                  :type="showResetConfirmPassword ? 'text' : 'password'"
                  placeholder="请再次输入新密码"
                  size="large"
                  @on-blur="checkResetConfirmPassword"
              >
                <Icon type="md-lock" slot="prefix"/>
              </Input>
              <span class="eye-icon" @click="showResetConfirmPassword = !showResetConfirmPassword">
                <Icon :type="showResetConfirmPassword ? 'md-eye' : 'md-eye-off'" />
              </span>
            </div>
            <div v-if="resetConfirmPasswordMessage" :class="['check-message', resetConfirmPasswordMessage.type]">
              {{ resetConfirmPasswordMessage.text }}
            </div>
          </FormItem>

          <!-- 重置密码按钮 -->
          <FormItem>
            <Button
                type="primary"
                long
                size="large"
                @click="handleResetPassword"
                :loading="resetLoading"
            >
              重置密码
            </Button>
          </FormItem>
        </Form>
      </div>
    </div>
  </Modal>
</template>

<script>

// 导入需要的组件和库
// 从View UI Plus库中导入模态框、表单相关组件
import { Modal, Form, FormItem, Input, Icon, Button } from 'view-ui-plus';
// 导入View UI Plus的样式文件
import 'view-ui-plus/dist/styles/viewuiplus.css';
// 导入API客户端
import apiClient from '@/api/index';
// 导入滑块验证码组件
import SliderCaptcha from '@/components/SliderCaptcha.vue';

// 导出组件定义
export default {
  // 组件名称，用于调试和递归组件
  name: 'LoginModal',

  // 注册使用的子组件，使它们在模板中可用
  components: {
    Modal,          // 模态框组件
    Form,           // 表单组件
    FormItem,       // 表单项目组件
    Input,          // 输入框组件
    Icon,           // 图标组件
    Button,         // 按钮组件
    SliderCaptcha   // 滑块验证码组件
  },

  // 定义组件接收的属性，实现父子组件通信
  props: {
    // 控制模态框显示/隐藏的属性，使用v-model双向绑定
    value: {
      type: Boolean,  // 属性类型为布尔值
      default: false  // 默认值为false（隐藏）
    }
  },

  // 组件数据，返回一个函数，确保每个实例都有独立的数据
  data() {
    return {
      activeTab: 'new',  // 当前激活的标签页，'new'表示注册，'old'表示登录，'reset'表示找回密码
      // 注册表单数据对象
      registerForm: {
        username: '',           // 用户名
        email: '',              // 邮箱
        password: '',           // 密码
        confirmPassword: '',    // 确认密码
        inviteCode: '',         // 邀请码（可选）
        verificationCode: '',    // 邮箱验证码
        captchaKey: '',          // 图形验证码key
        captchaCode: ''          // 图形验证码输入
      },
      // 登录表单数据对象
      loginForm: {
        username: '',     // 用户名
        password: '',     // 密码
        captchaKey: '',   // 图形验证码key
        captchaCode: '',  // 图形验证码输入
        sliderToken: ''   // 滑块验证码验证成功后返回的令牌
      },
      // 找回密码表单数据对象
      resetForm: {
        email: '',              // 邮箱
        verificationCode: '',   // 验证码
        newPassword: '',        // 新密码
        confirmPassword: ''     // 确认密码
      },
      isSendingCode: false,         // 是否正在发送验证码，用于禁用发送按钮
      verificationButtonText: '发送验证码', // 验证码按钮文本，根据状态变化
      registerLoading: false,       // 注册加载状态，用于显示加载动画
      loginLoading: false,          // 登录加载状态，用于显示加载动画
      resetLoading: false,          // 重置密码加载状态
      usernameCheckMessage: null,   // 用户名检查结果消息
      emailCheckMessage: null,      // 邮箱检查结果消息
      resetEmailMessage: null,      // 找回密码邮箱检查结果消息
      confirmPasswordMessage: null, // 注册确认密码校验消息
      resetConfirmPasswordMessage: null, // 找回密码确认密码校验消息
      countdown: 0,                 // 注册倒计时秒数
      countdownInterval: null,      // 注册倒计时定时器
      resetCountdown: 0,            // 找回密码倒计时秒数
      resetCountdownInterval: null, // 找回密码倒计时定时器
      isSendingResetCode: false,    // 是否正在发送找回密码验证码
      showRegisterPassword: false,  // 注册密码是否明文显示
      showRegisterConfirmPassword: false, // 注册确认密码是否明文显示
      showLoginPassword: false,     // 登录密码是否明文显示
      showResetNewPassword: false,  // 找回密码新密码是否明文显示
      showResetConfirmPassword: false, // 找回密码确认密码是否明文显示
      registerCaptchaImage: '',      // 注册图形验证码图片
      loginCaptchaImage: '',         // 登录图形验证码图片

      // 注册信息对象
      registerInfo: {
        new_user_bonus: '',  // 新用户注册送的点数
        allow_register: true,  // 是否允许注册
        allowed_email_domains: [],  // 允许注册的邮箱后缀白名单
      },
    };
  },

  // 计算属性，基于响应式依赖进行缓存
  computed: {
    // 控制模态框显示/隐藏的计算属性，实现v-model双向绑定
    visible: {
      // getter函数，返回props中的value值
      get() {
        return this.value;
      },
      // setter函数，当visible变化时触发input事件更新父组件的value
      set(val) {
        this.$emit('input', val);
      }
    }
  },

  // 组件销毁前的钩子函数
  beforeDestroy() {
    // 清除注册倒计时定时器，防止内存泄漏
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
    // 清除找回密码倒计时定时器，防止内存泄漏
    if (this.resetCountdownInterval) {
      clearInterval(this.resetCountdownInterval);
    }
  },
  mounted(){
    this.fetchRegisterInfo().then(()=>{
      // 根据是否允许注册设置默认标签
      if (!this.registerInfo.allow_register) {
        this.activeTab = 'old';
      }
    });
    document.title = '抖数君—账号登录';
    // 初始加载验证码
    this.refreshRegisterCaptcha();
    this.refreshLoginCaptcha();
  },
  // 组件方法，定义组件的各种功能
  methods: {
    // 在 methods 中添加
    async fetchRegisterInfo() {
      try {
        const response = await apiClient.get('admin/getregisterInfo');
        if (response.data.code === 200) {
          this.registerInfo = response.data.data;
        }
      } catch (error) {
        // 设置默认值以防API调用失败
        this.registerInfo = {
          new_user_bonus: 0,
          allow_register: true,
          allowed_email_domains: [],
        };
        console.error('获取getregisterInfo信息失败:', error);
      }
    },
    // 处理模态框关闭事件
    handleClose() {
      // 触发input事件，更新父组件的value为false（隐藏模态框）
      this.$emit('input', false);
      // 触发close事件，通知父组件模态框已关闭
      this.$emit('close');
    },

    // 获取注册图形验证码
    async refreshRegisterCaptcha() {
      try {
        const response = await apiClient.get('/captcha/generate');
        if (response.data.code === 200) {
          this.registerCaptchaImage = response.data.data.captcha_image;
          this.registerForm.captchaKey = response.data.data.captcha_key;
          this.registerForm.captchaCode = '';
        }
      } catch (error) {
        console.error('获取注册验证码失败:', error);
      }
    },

    // 获取登录图形验证码
    async refreshLoginCaptcha() {
      try {
        const response = await apiClient.get('/captcha/generate');
        if (response.data.code === 200) {
          this.loginCaptchaImage = response.data.data.captcha_image;
          this.loginForm.captchaKey = response.data.data.captcha_key;
          this.loginForm.captchaCode = '';
        }
      } catch (error) {
        console.error('获取登录验证码失败:', error);
      }
    },

    // 检查用户名是否已存在
    async checkUsername() {
      // 如果用户名为空，清空检查消息并返回
      if (!this.registerForm.username) {
        this.usernameCheckMessage = null;
        return;
      }

      // 简单的用户名格式验证（至少3个字符）
      if (this.registerForm.username.length < 3) {
        // 设置错误消息，提示用户名长度不足
        this.usernameCheckMessage = {
          text: '用户名至少需要3个字符',
          type: 'error'  // 错误类型，用于样式显示
        };
        return;
      }

      // 设置检查中状态，提示用户正在验证用户名
      this.usernameCheckMessage = {
        text: '检查中...',
        type: 'info'  // 信息类型，用于样式显示
      };

      try {
        // 向后端发送检查用户名请求
        // const response = await axios.post('/api/user/check-username', {
        //   username: this.registerForm.username  // 发送用户名参数
        // });
        const response = await apiClient.post('/user/check-username', {
          username: this.registerForm.username // 发送用户名参数
        });
        // 处理后端响应
        if (response.data.code === 200) {
          // 如果后端返回成功状态码
          if (response.data.data.exists) {
            // 用户名已存在，设置错误消息
            this.usernameCheckMessage = {
              text: '该用户名已被占用',
              type: 'error'
            };
          } else {
            // 用户名可用，设置成功消息
            this.usernameCheckMessage = {
              text: '用户名可以使用',
              type: 'success'  // 成功类型，用于样式显示
            };
          }
        } else {
          // 后端返回错误状态码，设置错误消息
          this.usernameCheckMessage = {
            text: response.data.msg || '检查用户名失败',
            type: 'error'
          };
        }
      } catch (error) {
        // 网络请求异常，设置错误消息
        this.usernameCheckMessage = {
          text: '网络错误，请稍后重试',
          type: 'error'
        };
        // 在控制台输出错误信息，便于调试
        console.error('Check username error:', error);
      }
    },

    // 检查邮箱后缀是否在白名单中
    checkEmailDomain() {
      const domains = this.registerInfo.allowed_email_domains;
      if (!domains || domains.length === 0) {
        return { allowed: true, message: '' };
      }
      const email = this.registerForm.email;
      if (!email) return { allowed: true, message: '' };
      const atIndex = email.lastIndexOf('@');
      if (atIndex === -1) return { allowed: true, message: '' };
      const domain = email.substring(atIndex + 1);
      if (domains.includes(domain)) {
        return { allowed: true, message: '' };
      }
      return {
        allowed: false,
        message: '只允许使用 ' + domains.join('、') + ' 后缀的邮箱注册'
      };
    },

    // 检查注册确认密码是否一致
    checkRegisterConfirmPassword() {
      if (!this.registerForm.confirmPassword) {
        this.confirmPasswordMessage = null;
        return;
      }
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.confirmPasswordMessage = {
          text: '两次输入的密码不一致',
          type: 'error'
        };
      } else {
        this.confirmPasswordMessage = {
          text: '密码一致',
          type: 'success'
        };
      }
    },

    // 检查找回密码确认密码是否一致
    checkResetConfirmPassword() {
      if (!this.resetForm.confirmPassword) {
        this.resetConfirmPasswordMessage = null;
        return;
      }
      if (this.resetForm.newPassword !== this.resetForm.confirmPassword) {
        this.resetConfirmPasswordMessage = {
          text: '两次输入的密码不一致',
          type: 'error'
        };
      } else {
        this.resetConfirmPasswordMessage = {
          text: '密码一致',
          type: 'success'
        };
      }
    },

    // 检查邮箱是否已存在
    async checkEmail() {
      // 如果邮箱为空，清空检查消息并返回
      if (!this.registerForm.email) {
        this.emailCheckMessage = null;
        return;
      }

      // 邮箱格式验证，使用正则表达式检查邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.registerForm.email)) {
        // 邮箱格式不正确，设置错误消息
        this.emailCheckMessage = {
          text: '请输入正确的邮箱格式',
          type: 'error'
        };
        return;
      }

      // 验证邮箱后缀是否在白名单中
      const domainCheck = this.checkEmailDomain();
      if (!domainCheck.allowed) {
        this.emailCheckMessage = {
          text: domainCheck.message,
          type: 'error'
        };
        return;
      }

      // 设置检查中状态，提示用户正在验证邮箱
      this.emailCheckMessage = {
        text: '检查中...',
        type: 'info'
      };

      try {
        // 向后端发送检查邮箱请求
        // const response = await axios.post('/api/user/check-email', {
        //   email: this.registerForm.email  // 发送邮箱参数
        // });
        const response = await apiClient.post('/user/check-email', {
          email: this.registerForm.email // 发送邮箱参数
        });
        // 处理后端响应
        if (response.data.code === 200) {
          // 如果后端返回成功状态码
          if (response.data.data.exists) {
            // 邮箱已注册，设置错误消息
            this.emailCheckMessage = {
              text: '该邮箱已被注册',
              type: 'error'
            };
          } else {
            // 邮箱可用，设置成功消息
            this.emailCheckMessage = {
              text: '邮箱可以使用',
              type: 'success'
            };
          }
        } else {
          // 后端返回错误状态码，设置错误消息
          this.emailCheckMessage = {
            text: response.data.msg || '检查邮箱失败',
            type: 'error'
          };
        }
      } catch (error) {
        // 网络请求异常，设置错误消息
        this.emailCheckMessage = {
          text: '网络错误，请稍后重试',
          type: 'error'
        };
        // 在控制台输出错误信息，便于调试
        console.error('Check email error:', error);
      }
    },

    // 发送验证码
    async sendVerificationCode() {
      // 如果正在发送验证码或倒计时未结束，则直接返回，防止重复点击
      if (this.isSendingCode || this.countdown > 0) return;

      // 检查邮箱是否已填写
      if (!this.registerForm.email) {
        // 显示警告消息，提示用户输入邮箱
        this.$Message.warning('请输入邮箱');
        return;
      }

      // 邮箱格式验证，使用正则表达式检查邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.registerForm.email)) {
        // 显示警告消息，提示用户输入正确格式的邮箱
        this.$Message.warning('请输入正确的邮箱格式');
        return;
      }

      // 验证邮箱后缀是否在白名单中
      const domainCheck = this.checkEmailDomain();
      if (!domainCheck.allowed) {
        this.$Message.warning(domainCheck.message);
        return;
      }

      // 设置发送状态，防止重复点击
      this.isSendingCode = true;
      // 更新按钮文本，提示用户正在发送
      this.verificationButtonText = '发送中...';

      try {
        // 调用实际的发送验证码API
        // const response = await axios.post('/api/user/sendcode', {
        //   user_email: this.registerForm.email  // 发送用户邮箱参数
        // });
        const response = await apiClient.post('/user/sendcode', {
          user_email: this.registerForm.email  // 发送用户邮箱参数
        });
        // 处理后端响应
        if (response.data.code === 200) {
          // 发送成功，显示成功消息
          this.$Message.success('验证码已发送到您的邮箱');

          // 启动倒计时（60秒）
          this.startCountdown(60);
        } else {
          // 发送失败，显示错误消息
          this.$Message.error(response.data.msg || '发送验证码失败');
        }
      } catch (error) {
        // 处理网络请求异常
        console.error('Send verification code error:', error);
        if (error.response && error.response.data) {
          // 如果有后端返回的错误信息，显示该信息
          this.$Message.error(error.response.data.msg || '发送验证码失败');
        } else {
          // 网络请求失败，显示通用错误消息
          this.$Message.error('网络请求失败，请稍后重试');
        }
      } finally {
        // 重置发送状态，无论成功或失败都要执行
        this.isSendingCode = false;
        // 恢复按钮文本
        this.verificationButtonText = '发送验证码';
      }
    },

    // 启动倒计时
    startCountdown(seconds) {
      // 设置初始倒计时秒数
      this.countdown = seconds;

      // 启动定时器，每秒减少1秒
      this.countdownInterval = setInterval(() => {
        // 倒计时减1
        this.countdown--;

        // 如果倒计时结束
        if (this.countdown <= 0) {
          // 清除定时器
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      }, 1000); // 每1000毫秒（1秒）执行一次
    },

    // 处理用户注册
    async handleRegister() {
      // 基本验证：检查必填项（用户名、邮箱、密码）
      if (!this.registerForm.username || !this.registerForm.email || !this.registerForm.password) {
        // 显示警告消息，提示用户填写必填项
        this.$Message.warning('请填写必填项');
        return;
      }

      // 图形验证码验证
      if (!this.registerForm.captchaCode) {
        this.$Message.warning('请输入图形验证码');
        return;
      }

      // 邮箱格式验证，使用正则表达式检查邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.registerForm.email)) {
        // 显示警告消息，提示用户输入正确格式的邮箱
        this.$Message.warning('请输入正确的邮箱格式');
        return;
      }

      // 验证邮箱后缀是否在白名单中
      const domainCheck = this.checkEmailDomain();
      if (!domainCheck.allowed) {
        this.$Message.warning(domainCheck.message);
        return;
      }

      // 密码强度验证（至少6位）
      if (this.registerForm.password.length < 6) {
        // 显示警告消息，提示用户密码长度不足
        this.$Message.warning('密码至少需要6位');
        return;
      }

      // 确认密码验证
      if (!this.registerForm.confirmPassword) {
        this.$Message.warning('请再次输入密码');
        return;
      }
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.$Message.warning('两次输入的密码不一致');
        return;
      }

      // 验证码验证，检查是否已输入验证码
      if (!this.registerForm.verificationCode) {
        // 显示警告消息，提示用户输入验证码
        this.$Message.warning('请输入验证码');
        return;
      }

      // 检查用户名是否已被占用
      if (this.usernameCheckMessage && this.usernameCheckMessage.type === 'error') {
        // 显示警告消息，提示用户重新输入用户名
        this.$Message.warning('用户名不可用，请重新输入');
        return;
      }

      // 检查邮箱是否已被注册
      if (this.emailCheckMessage && this.emailCheckMessage.type === 'error') {
        // 显示警告消息，提示用户重新输入邮箱
        this.$Message.warning('邮箱已被注册，请重新输入');
        return;
      }

      // 设置注册加载状态，显示加载动画
      this.registerLoading = true;

      try {
        // 向注册API发送请求，包含验证码
        // const response = await axios.post('/api/user/register', {
        //   username: this.registerForm.username,      // 用户名
        //   email: this.registerForm.email,            // 邮箱
        //   password: this.registerForm.password,      // 密码
        //   email_code: this.registerForm.verificationCode  // 验证码
        // });
        const response = await apiClient.post('/user/register', {
          username: this.registerForm.username,      // 用户名
          email: this.registerForm.email,            // 邮箱
          password: this.registerForm.password,      // 密码
          email_code: this.registerForm.verificationCode,  // 邮箱验证码
          captcha_key: this.registerForm.captchaKey,        // 图形验证码key
          captcha_code: this.registerForm.captchaCode       // 图形验证码
        });
        // 处理后端响应
        if (response.data.code === 200) {
          // 注册成功
          this.$Message.success('注册成功');
          // 触发register-success事件，传递注册成功后的用户信息
          this.$emit('register-success', {
            email: this.registerForm.email,           // 邮箱
            username: this.registerForm.username,     // 用户名
            balance: response.data.data.balance || 0, // 余额（默认为0）
            token: response.data.data.token,          // 认证令牌
          });
          // 关闭模态框
          this.visible = false;
        } else {
          // 注册失败，显示错误消息
          this.$Message.error(response.data.msg || '注册失败');
          // 刷新图形验证码
          this.refreshRegisterCaptcha();
        }
      } catch (error) {
        // 处理网络请求异常
        if (error.response && error.response.data) {
          // 如果有后端返回的错误信息，显示该信息
          this.$Message.error(error.response.data.msg || error.response.data.message || '注册失败');
        } else {
          // 网络请求失败，显示通用错误消息
          this.$Message.error('注册请求失败，请稍后重试');
        }
        // 在控制台输出错误信息，便于调试
        console.error('Register error:', error);
      } finally {
        // 重置注册加载状态，无论成功或失败都要执行
        this.registerLoading = false;
      }
    },

    // 滑块验证码验证成功回调
    handleSliderSuccess(token) {
      // 保存后端返回的滑块验证令牌
      this.loginForm.sliderToken = token;
    },

    // 滑块验证码验证失败回调
    handleSliderFail() {
      // 清空已保存的滑块验证令牌
      this.loginForm.sliderToken = '';
    },

    // 重置登录滑块验证码组件
    resetLoginSliderCaptcha() {
      // 清空滑块验证令牌
      this.loginForm.sliderToken = '';
      // 调用子组件方法重置滑块状态
      if (this.$refs.loginSliderCaptcha) {
        this.$refs.loginSliderCaptcha.resetSlider();
      }
    },

    // 处理用户登录
    async handleLogin() {
      // 验证账号和密码是否填写（账号支持用户名或邮箱）
      if (!this.loginForm.username || !this.loginForm.password) {
        // 显示警告消息，提示用户填写账号和密码
        this.$Message.warning('请填写账号和密码');
        return;
      }

      // 图形验证码验证
      if (!this.loginForm.captchaCode) {
        this.$Message.warning('请输入图形验证码');
        return;
      }

      // 滑块验证码验证：必须完成滑块验证才能登录
      if (!this.loginForm.sliderToken) {
        this.$Message.warning('请先完成滑块验证');
        return;
      }

      // 设置登录加载状态，显示加载动画
      this.loginLoading = true;

      try {
        // 使用username和password向后端发送登录请求
        const response = await apiClient.post('/user/login', {
          username: this.loginForm.username,  // 用户名
          password: this.loginForm.password,   // 密码
          captcha_key: this.loginForm.captchaKey,   // 图形验证码key
          captcha_code: this.loginForm.captchaCode, // 图形验证码
          slider_token: this.loginForm.sliderToken  // 滑块验证码令牌
        });
        // 处理后端响应
        if (response.data.code === 200) {
          // 登录成功
          this.$Message.success('登录成功');
          // 触发login-success事件，传递登录成功后的用户信息
          this.$emit('login-success', {
            email: response.data.data.email,     // 邮箱
            balance: response.data.data.balance, // 余额
            username: response.data.data.username, // 用户名
            token: response.data.data.token,     // 认证令牌
          });
          // 关闭模态框
          this.visible = false;
        } else {
          // 登录失败，显示错误消息
          this.$Message.error(response.data.msg || '登录失败');
          // 刷新图形验证码
          this.refreshLoginCaptcha();
          // 重置滑块验证码，要求用户重新完成验证
          this.resetLoginSliderCaptcha();
        }
      } catch (error) {
        // 处理网络请求异常
        if (error.response && error.response.data) {
          // 如果有后端返回的错误信息，显示该信息
          this.$Message.error(error.response.data.msg || '登录失败');
        } else {
          // 网络请求失败，显示通用错误消息
          this.$Message.error('登录请求失败，请稍后重试');
        }
        // 在控制台输出错误信息，便于调试
        console.error('Login error:', error);
        // 重置滑块验证码
        this.resetLoginSliderCaptcha();
      } finally {
        // 重置登录加载状态，无论成功或失败都要执行
        this.loginLoading = false;
      }
    },

    // ========== 找回密码相关方法 ==========
    
    // 检查找回密码邮箱是否已注册
    async checkResetEmail() {
      if (!this.resetForm.email) {
        this.resetEmailMessage = null;
        return;
      }

      // 邮箱格式验证
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.resetForm.email)) {
        this.resetEmailMessage = {
          text: '请输入正确的邮箱格式',
          type: 'error'
        };
        return;
      }

      this.resetEmailMessage = {
        text: '检查中...',
        type: 'info'
      };

      try {
        const response = await apiClient.post('/reset_password/checkEmailForReset', {
          email: this.resetForm.email
        });
        
        if (response.data.code === 200) {
          if (response.data.data.exists) {
            this.resetEmailMessage = {
              text: '邮箱已注册，可以重置密码',
              type: 'success'
            };
          } else {
            this.resetEmailMessage = {
              text: '该邮箱未注册',
              type: 'error'
            };
          }
        } else {
          this.resetEmailMessage = {
            text: response.data.msg || '检查邮箱失败',
            type: 'error'
          };
        }
      } catch (error) {
        this.resetEmailMessage = {
          text: '网络错误，请稍后重试',
          type: 'error'
        };
        console.error('Check reset email error:', error);
      }
    },

    // 发送找回密码验证码
    async sendResetCode() {
      // 验证邮箱
      if (!this.resetForm.email) {
        this.$Message.warning('请先输入邮箱');
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.resetForm.email)) {
        this.$Message.warning('请输入正确的邮箱格式');
        return;
      }

      // 设置发送状态
      this.isSendingResetCode = true;

      try {
        const response = await apiClient.post('/reset_password/sendResetCode', {
          email: this.resetForm.email
        });

        if (response.data.code === 200) {
          this.$Message.success('验证码已发送到您的邮箱');
          // 开始倒计时
          this.startResetCountdown();
        } else {
          this.$Message.error(response.data.msg || '发送验证码失败');
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.$Message.error(error.response.data.msg || '发送验证码失败');
        } else {
          this.$Message.error('发送验证码失败，请稍后重试');
        }
        console.error('Send reset code error:', error);
      } finally {
        this.isSendingResetCode = false;
      }
    },

    // 开始找回密码倒计时
    startResetCountdown() {
      this.resetCountdown = 60;
      this.resetCountdownInterval = setInterval(() => {
        this.resetCountdown--;

        if (this.resetCountdown <= 0) {
          clearInterval(this.resetCountdownInterval);
          this.resetCountdownInterval = null;
        }
      }, 1000);
    },

    // 处理重置密码
    async handleResetPassword() {
      // 验证必填项
      if (!this.resetForm.email || !this.resetForm.verificationCode || 
          !this.resetForm.newPassword || !this.resetForm.confirmPassword) {
        this.$Message.warning('请填写所有必填项');
        return;
      }

      // 验证邮箱格式
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.resetForm.email)) {
        this.$Message.warning('请输入正确的邮箱格式');
        return;
      }

      // 验证密码强度
      if (this.resetForm.newPassword.length < 6) {
        this.$Message.warning('密码至少需要6位');
        return;
      }

      // 验证密码包含数字和字母
      const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;
      if (!passwordRegex.test(this.resetForm.newPassword)) {
        this.$Message.warning('密码必须包含数字和字母');
        return;
      }

      // 验证两次密码一致
      if (this.resetForm.newPassword !== this.resetForm.confirmPassword) {
        this.$Message.warning('两次输入的密码不一致');
        return;
      }

      // 设置加载状态
      this.resetLoading = true;

      try {
        const response = await apiClient.post('/reset_password/resetPassword', {
          email: this.resetForm.email,
          email_code: this.resetForm.verificationCode,
          new_password: this.resetForm.newPassword
        });

        if (response.data.code === 200) {
          this.$Message.success('密码重置成功，请使用新密码登录');
          // 清空表单
          this.resetForm = {
            email: '',
            verificationCode: '',
            newPassword: '',
            confirmPassword: ''
          };
          this.resetEmailMessage = null;
          // 切换到登录标签
          this.activeTab = 'old';
        } else {
          this.$Message.error(response.data.msg || '密码重置失败');
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.$Message.error(error.response.data.msg || '密码重置失败');
        } else {
          this.$Message.error('密码重置失败，请稍后重试');
        }
        console.error('Reset password error:', error);
      } finally {
        this.resetLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* 认证容器样式 */
.auth-container {
  padding: 2px 0;  /* 上下内边距20px，左右无内边距 */
}

/* 标签链接容器样式 */
.tab-links {
  display: flex;           /* 弹性布局，使子元素水平排列 */
  justify-content: center; /* 水平居中对齐 */
  margin-bottom: 15px;     /* 下外边距20px，与下方元素保持距离 */

}

/* 标签链接样式 */
.tab-links span {
  cursor: pointer;         /* 鼠标指针样式，提示可点击 */
  margin: 0 15px;          /* 左右外边距10px，上下无外边距 */
  padding: 12px 10px;       /* 内边距，上下5px，左右10px */
  font-size: 15px;         /* 字体大小14px */
  border-radius: 4px;      /* 圆角边框，半径4px */
  transition: all 0.3s;    /* 过渡动画，所有属性变化在0.3秒内完成 */
}

/* 激活状态的标签链接样式 */
.tab-links span.active {
  background-color: #2d8cf0;  /* 背景色为蓝色 */
  color: white;               /* 文字颜色为白色 */
}

/* 验证码容器样式 */
.verification-code {
  display: flex;        /* 弹性布局，使子元素水平排列 */
  align-items: center;  /* 垂直居中对齐 */
}

/* 图形验证码行样式 */
.captcha-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* 图形验证码图片容器 */
.captcha-image-wrapper {
  width: 120px;
  height: 40px;
  cursor: pointer;
  border: 1px solid #dcdee2;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}

.captcha-image-wrapper:hover {
  border-color: #2d8cf0;
}

.captcha-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.captcha-loading {
  font-size: 12px;
  color: #999;
}

/* 认证模态框中的表单项样式 */
.auth-modal .ivu-form-item {
  margin-bottom: 15px;  /* 下外边距15px，减少表单项之间的间距 */
}

/* 登录提示信息样式 */
.login-tip {
  text-align: center;  /* 文本居中对齐 */
  margin-bottom: 20px; /* 下外边距20px，与下方元素保持距离 */
  color: #666;         /* 文字颜色为灰色 */
}

/* 登录提示信息段落样式 */
.login-tip p {
  margin: 0;        /* 无外边距 */
  font-size: 14px;  /* 字体大小14px */
}

/* 找回密码提示信息样式 */
.reset-tip {
  text-align: center;  /* 文本居中对齐 */
  margin-bottom: 20px; /* 下外边距20px，与下方元素保持距离 */
  padding: 12px;
  background-color: #d1ecf1;
  border-radius: 4px;
  border-left: 3px solid #17a2b8;
}

.reset-tip p {
  margin: 0;        /* 无外边距 */
  font-size: 14px;  /* 字体大小14px */
  color: #0c5460;   /* 文字颜色为深蓝色 */
}

/* 密码输入框容器 */
.password-input-wrapper {
  position: relative;
}

/* 眼睛图标 */
.eye-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 2;
  color: #808695;
  font-size: 18px;
}

.password-input-wrapper .ivu-input {
  padding-right: 35px;
}

/* 检查消息样式 */
.check-message {
  font-size: 12px;      /* 字体大小12px，较小的文字 */
  margin-top: 5px;      /* 上外边距5px，与输入框保持距离 */
  padding: 3px 5px;     /* 内边距，上下3px，左右5px */
  border-radius: 3px;   /* 圆角边框，半径3px */
}

/* 成功状态的检查消息样式 */
.check-message.success {
  color: #52c41a;              /* 文字颜色为绿色 */
  background-color: #f6ffed;   /* 背景色为浅绿色 */
  border: 1px solid #b7eb8f;   /* 边框为绿色 */
}

/* 错误状态的检查消息样式 */
.check-message.error {
  color: #ff4d4f;              /* 文字颜色为红色 */
  background-color: #fff2f0;   /* 背景色为浅红色 */
  border: 1px solid #ffccc7;   /* 边框为红色 */
}

/* 信息状态的检查消息样式 */
.check-message.info {
  color: #1890ff;              /* 文字颜色为蓝色 */
  background-color: #e6f7ff;   /* 背景色为浅蓝色 */
  border: 1px solid #91d5ff;   /* 边框为蓝色 */
}
</style>
